import { baseApi } from "@/core/api/baseApi";

// ─── Global Search API (docs/GLOBAL_SEARCH_UI_GUIDE.md) ───
// Base URL already ends in `/api`, so paths are `/search/...`.
// Results + actions are role-scoped server-side — the client never re-filters by role.

export type EntityType =
  | "CLIENT"
  | "EMPLOYER"
  | "GROUP"
  | "DIVISION"
  | "LOCATION"
  | "BILLING_ACCOUNT"
  | "AGENCY"
  | "AGENT";

/** BILLING_ACCOUNT hits: how the account came to be (a filter facet). */
export type AccountKind = "CONSOLIDATED" | "PER_PREFERENCE" | "MANUAL";

export interface SearchHit {
  type: EntityType;
  level: string;
  id: string;
  brokerId?: string | null;
  name: string;
  status?: string | null;
  clientId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  clientName?: string;
  employerName?: string;
  groupName?: string;
  divisionName?: string;
  locationName?: string;
  hierarchyPath: string;
  detailRoute?: string;
  /** Billing account at EXACTLY this hit's scope, when one exists (drives the BILLING_ACCOUNT action). */
  billingAccountId?: number | null;
  billingAccountRoute?: string | null;
  /** BILLING_ACCOUNT hits only. */
  accountKind?: AccountKind | null;
  availableActions: string[];
}

export interface GlobalSearchResponse {
  query: string;
  totalCount: number;
  countsByType: Record<string, number>;
  results: SearchHit[];
}

export interface ActionDescriptor {
  key: string;
  label: string;
  kind: "NAVIGATE" | "API";
  method?: "GET" | "POST";
  endpoint?: string;
  paramTarget?: "QUERY" | "BODY";
  params?: Record<string, string>; // apiParam -> hit field, or "$month"
  fixedParams?: Record<string, string>;
  routeField?: string;
  requiresMonth: boolean;
  monthFormat?: "YYYYMM" | "MM/dd/yyyy";
  writeAction: boolean;
}

export interface ActionCatalog {
  role: string | null;
  actions: Record<EntityType, ActionDescriptor[]>;
}

/**
 * A concrete API call assembled from an {@link ActionDescriptor} + a
 * {@link SearchHit}. `url` mirrors the dev catalog endpoint (which already
 * includes the `/api` prefix); the leading `/api` is stripped when routing
 * through {@link baseApi} because the axios client's base URL already ends in
 * `/api`, so the final URL is identical to the dev `${ORIGIN}${url}` request.
 */
export interface BuiltRequest {
  method: "GET" | "POST";
  url: string;
  body?: Record<string, unknown>;
}

export const searchApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    // Ranked, mixed result list. `limit` = max hits per entity type.
    getGlobalSearch: build.query<GlobalSearchResponse, { term: string; limit?: number }>({
      query: ({ term, limit = 20 }) => ({
        url: `/search/global`,
        params: { query: term, limit },
      }),
    }),

    // action key → real endpoint/route map; cached for the session.
    getActionCatalog: build.query<ActionCatalog, void>({
      query: () => ({ url: `/search/action-catalog` }),
      keepUnusedDataFor: 3600,
    }),

    // Executes a catalog-driven API action (the old ActionPanel `fetch`).
    // The base URL already ends in `/api`, so the descriptor's `/api` prefix is
    // stripped here to keep the resolved URL byte-for-byte identical to dev.
    runSearchAction: build.mutation<unknown, BuiltRequest>({
      query: ({ method, url, body }) => ({
        url: url.replace(/^\/api(?=\/)/, ""),
        method,
        ...(body !== undefined ? { body } : {}),
      }),
    }),
  }),
});

export const { useGetGlobalSearchQuery, useGetActionCatalogQuery, useRunSearchActionMutation } =
  searchApi;
